import { useQueryClient } from "@tanstack/react-query";

import { getFullName } from "@vega-api/custom/utils/user";
import { getGetDealTrackingForGpQueryKey } from "@vega-api/generated/coinvest-deal-tracking/coinvest-deal-tracking";
import { type DealTrackingLimitedPartnerBreakdown } from "@vega-api/generated/types/dealTrackingLimitedPartnerBreakdown";
import { useBoolean } from "@vega-core/hooks/useBoolean";

import { PersonCell } from "../../PersonCell/PersonCell";
import { OpportunityOwnerModal } from "../../ProspectsTable/OpportunityOwnerModal";

type OpportunityOwnerCellProps = {
  row: DealTrackingLimitedPartnerBreakdown;
  dealId: string;
  canEdit: boolean;
};

export const OpportunityOwnerCell = ({
  row,
  dealId,
  canEdit,
}: OpportunityOwnerCellProps) => {
  const queryClient = useQueryClient();
  const {
    isOpen: isModalOpen,
    setTrue: openModal,
    setFalse: closeModal,
  } = useBoolean(false);

  const owner = row.dealOpportunityOwner;
  const content = (
    <PersonCell
      dataTestId={`opportunity-owner-cell-${row.dealOpportunityId}`}
      email={owner?.email}
      name={owner ? getFullName(owner.fullName) : undefined}
    />
  );

  return (
    <>
      {canEdit ? (
        <button
          className="inline-flex items-center h-full w-full focus-outline-themed"
          data-testid={`opportunity-owner-trigger-${row.dealOpportunityId}`}
          onClick={openModal}
          type="button"
        >
          {content}
        </button>
      ) : (
        <div className="flex items-center h-full">{content}</div>
      )}

      {isModalOpen ? (
        <OpportunityOwnerModal
          closeModal={closeModal}
          dealId={dealId}
          onSuccess={() =>
            queryClient.invalidateQueries({
              queryKey: getGetDealTrackingForGpQueryKey(dealId),
            })
          }
          open={isModalOpen}
          selectedProspects={[
            {
              id: row.dealShortlistItemId,
              limitedPartner: { name: row.limitedPartnerName },
            },
          ]}
        />
      ) : null}
    </>
  );
};
